import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { handleGetRequest } from '../../../service/GetTemplate';
import { formatTimeFromMinutes } from '../../../utilities/formatTime';
import { endPoints } from '../../../Config';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import { translateUiText } from '../../../utilities/siteTranslations';
import ReportExportButton from '../ReportExportButton';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const MachineHistory = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const dispatch = useDispatch();
    const requestVersion = useRef(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Load customers for filter
        const loadCustomers = async () => {
            try {
                const response = await handleGetRequest(endPoints.getallUsers, true, false, dispatch);
                if (response?.data) {
                    // Filter to get only customers (users with customer role or all users)
                    const customerOptions = response.data
                        .filter((user) => {
                            // You can add role filtering here if needed
                            return user;
                        })
                        .map((user) => ({
                            label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || t('Unknown User'),
                            value: user._id || user.id
                        }));
                    setCustomers([{ label: t('All Customers'), value: null }, ...customerOptions]);
                }
            } catch (e) {
                console.error('Failed to load customers', e);
            }
        };

        loadCustomers();
    }, [dispatch, preferredLanguage]);

    const loadHistory = async (customerId = null, text = '', allPages = false) => {
        const version = ++requestVersion.current;
        setLoading(true);
        try {
            const records = [];
            const pageSize = 200;
            let skip = 0;
            while (true) {
                const params = new URLSearchParams({ limit: String(pageSize), skip: String(skip) });
                if (customerId) params.set('customerId', customerId);
                if (text.trim()) params.set('searchText', text.trim());
                const response = await handleGetRequest(`machineLog/history?${params}`, false, false, dispatch);
                if (!Array.isArray(response?.data)) throw new Error('Could not load machine history');
                records.push(...response.data);
                if (!allPages || response.data.length < pageSize) break;
                skip += response.data.length;
            }
                const list = records.map((d, idx) => {
                    const parts = [];
                    if (d.detail) {
                        if (d.detail.part) {
                            parts.push(`${d.detail.part.sku || d.detail.part.description || ''}${d.detail.quantity ? ` (${d.detail.quantity})` : ''}`.trim());
                        }
                        if (Array.isArray(d.detail.partsUsed)) {
                            d.detail.partsUsed.forEach((p) => {
                                parts.push(`${p.sku || p.sparePart?.sku || p.description || ''}${p.quantity ? ` (${p.quantity})` : ''}`.trim());
                            });
                        }
                    }

                    // Get technician name - now populated from backend
                    const tech = d.detail?.technicianName || 
                        (d.detail?.technician && typeof d.detail?.technician === 'object' && d.detail.technician.firstName
                            ? `${d.detail.technician.firstName} ${d.detail.technician.lastName || ''}`.trim()
                            : (typeof d.detail?.technician === 'string' ? d.detail.technician : d.detail?.technician?._id || '')) ||
                        (d.meta && d.meta.writtenBy) || 
                        '';

                    // Get machine location - now populated from backend
                    const location = d.machine?.location || '-';
                    const machineId = d.machine?.serialNumber || d.machine?.deviceId || '-';

                    // Format event type - convert underscores to spaces and capitalize
                    const eventType = d.eventType || d.eventSubType || '';
                    const formattedEventType = eventType
                        .replace(/_/g, ' ')
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ');

                    return {
                        id: d._id || idx,
                        machineId: machineId,
                        location: location,
                        date: d.timestamp ? new Date(d.timestamp).toISOString().split('T')[0] : '',
                        eventType: formattedEventType,
                        description: d.summary || d.detail?.notes || '',
                        partsUsed: parts.join(', ') || t('None'),
                        technician: tech,
                        cost: Number(d.detail?.totalCost ?? d.detail?.cost ?? 0) || 0,
                        duration: formatTimeFromMinutes(d.detail?.durationMinutes, preferredLanguage),
                        raw: d
                    };
                });
            if (version === requestVersion.current) setFilteredHistory(list);
            return list;
        } catch (error) {
            if (version === requestVersion.current) setFilteredHistory([]);
            throw error;
        } finally {
            if (version === requestVersion.current) setLoading(false);
        }
    };

    useEffect(() => {
        loadHistory(selectedCustomer, searchText).catch(() => {});
        return () => { requestVersion.current += 1; };
        // Search text is submitted using the Search button; customer changes preserve it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomer]);

    const filterByText = () => loadHistory(selectedCustomer, searchText).catch(() => {});

    const eventTypeBodyTemplate = (rowData) => {
        const typeColors = {
            'Repair': '#F44336',
            'Preventive Maintenance': '#4CAF50',
            'Movement': '#2196F3',
            'Installation': '#FF9800',
            'Removal': '#9C27B0',
            'Spare Usage': '#FF9800',
            'Spare usage': '#FF9800'
        };
        return <span style={{ color: typeColors[rowData.eventType] || '#000', fontWeight: 'bold' }}>{t(rowData.eventType)}</span>;
    };

    const costBodyTemplate = (rowData) => {
        return `$${rowData.cost?.toFixed(2) || '0.00'}`;
    };

    const handleCustomerChange = (e) => {
        setSelectedCustomer(e.value);
    };

    const handleClearFilters = () => {
        setSearchText('');
        setSelectedCustomer(null);
        loadHistory(null, '').catch(() => {});
    };

    return (
        <div>
            <div className="card mb-4">
                <h4 className="mb-3">{t('Filters')}</h4>
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">{t('Filter by Customer')}</label>
                        <Dropdown 
                            value={selectedCustomer} 
                            options={customers} 
                            onChange={handleCustomerChange} 
                            placeholder={t('Select Customer')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">{t('Filter by Machine ID')}</label>
                        <div className="flex gap-2">
                            <InputText
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder={t('Search text across all machines')}
                                className="w-full"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') filterByText();
                                }}
                            />
                            <Button label={t('Search')} icon="pi pi-search" onClick={filterByText} />
                            <Button
                                label={t('Clear')}
                                icon="pi pi-times"
                                onClick={handleClearFilters}
                                className="p-button-secondary"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4>{t('Machine History')}</h4>
                    <ReportExportButton disabled={loading} getRows={() => loadHistory(selectedCustomer, searchText, true)} filename="Historial_de_maquinas" columns={[
                        { key: 'machineId', header: t('Machine ID') },
                        { key: 'location', header: t('Location'), width: 30 },
                        { key: 'date', header: t('Date') },
                        { key: 'eventType', header: t('Event Type'), formatter: t },
                        { key: 'description', header: t('Description'), width: 50 },
                        { key: 'partsUsed', header: t('Parts Used'), width: 30 },
                        { key: 'technician', header: t('Technician') },
                        { key: 'cost', header: t('Cost') },
                        { key: 'duration', header: t('Duration'), width: 25 }
                    ]} />
                </div>
                <DataTable 
                    value={filteredHistory}
                    loading={loading}
                    paginator 
                    rows={10} 
                    rowsPerPageOptions={[5, 10, 20]} 
                    emptyMessage={t('No history found.')} 
                    responsiveLayout="scroll"
                >
                    <Column field="machineId" header={t('Machine ID')} />
                    <Column field="location" header={t('Location')} />
                    <Column field="date" header={t('Date')} />
                    <Column field="eventType" header={t('Event Type')} body={eventTypeBodyTemplate} />
                    <Column field="description" header={t('Description')} />
                    <Column field="partsUsed" header={t('Parts Used')} />
                    <Column field="technician" header={t('Technician')} />
                    <Column field="cost" header={t('Cost')} body={costBodyTemplate} />
                    <Column field="duration" header={t('Duration')} />
                </DataTable>
            </div>
        </div>
    );
};

export default MachineHistory;
